// src/components/AttivitaScadenzeWidgetContainer.jsx
import React, { useEffect, useMemo, useState } from "react";
import AttivitaScadenzeWidget from "./AttivitaScadenzeWidget";

function apiMonthKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
}
function firstDay(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function lastDay(date) {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

async function apiGet(url) {
    const r = await fetch(url, { credentials: "include" });
    const j = await r.json().catch(() => null);
    if (!j || j.ok !== true) throw new Error(j?.error || "Errore API");
    return j;
}
async function apiPost(url, body) {
    const r = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => null);
    if (!j || j.ok !== true) throw new Error(j?.error || "Errore API");
    return j;
}

// mapping: widget priority -> DB priority 1..4
function mapPriorityToDb(p) {
    if (Number.isFinite(Number(p))) return Math.min(4, Math.max(1, Number(p)));
    if (p === "high") return 4;     // urgente
    if (p === "medium") return 3;   // alta / in scadenza
    return 2;                       // normale
}
function mapDbPriorityToWidget(n) {
    const p = Number(n || 2);
    if (p >= 4) return "high";
    if (p === 3) return "medium";
    return "normal";
}

// mapping DB -> widget item
function mapTaskRowToWidgetItem(t) {
    const dateStr = t.target_date || t.due_date || new Date().toISOString().slice(0, 10);
    return {
        id: t.id,
        label: t.title,
        description: t.description || null,
        date: dateStr,
        priority: mapDbPriorityToWidget(t.priority),
        urgent: Number(t.priority) >= 4,
        warning: Number(t.priority) === 3,
        metaDate: t.due_date ? `Scade: ${t.due_date}` : null,
        assignee: t.assigned_to
            ? (t.assigned_to_name || t.assigned_to_email || `Utente #${t.assigned_to}`)
            : null,
        raw: t,
    };
}

function abbattimentoRegistroUrl(item, complete = false) {
    const ref = item?.raw?.source_ref || item?.source_ref || null;
    const params = new URLSearchParams();
    if (ref) params.set("abbattimento_id", String(ref));
    if (complete) params.set("complete", "1");
    const query = params.toString();
    return query ? `/abbattimenti/registro?${query}` : "/abbattimenti/registro";
}

function taskActionUrl(item, complete = false) {
    const module = item?.raw?.source_module || "";
    if (module === "abbattimenti") return abbattimentoRegistroUrl(item, complete);

    const links = {
        reminder_igiene_personale: "/igiene-personale",
        reminder_temperature_frigo: "/frighi",
        reminder_analisi_laboratorio: "/api/documenti_azienda.php?cat=analisi_laboratorio",
        reminder_formazione: "/api/documenti_azienda.php?cat=corso_alimentaristi",
        reminder_audit_esterno: "/api/documenti_azienda.php?cat=audit_controllo_esterno",
        reminder_audit_haccp: "/audit-haccp",
        reminder_sanificazione: "/sanificazione",
        reminder_infestanti: "/infestanti",
        reminder_manutenzione: "/manutenzioni",
    };

    return links[module] || null;
}

export default function AttivitaScadenzeWidgetContainer() {
    const [month, setMonth] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const [tasksOpen, setTasksOpen] = useState([]);
    const [tasksDone, setTasksDone] = useState([]);
    const [dotsMap, setDotsMap] = useState(new Map());

    const monthKey = useMemo(() => apiMonthKey(month), [month]);
    const from = useMemo(() => firstDay(month), [month]);
    const to = useMemo(() => lastDay(month), [month]);

    async function reloadAll() {
        setLoading(true);
        setErr("");
        try {
            // La sincronizzazione aggiorna i promemoria automatici, ma un suo
            // eventuale errore non deve impedire la lettura delle attività esistenti.
            try {
                await apiGet(`/api/tasks/reminders_sync.php?_=${Date.now()}`);
            } catch (syncError) {
                console.warn("Sincronizzazione promemoria non disponibile", syncError);
            }

            const [open, done, dots] = await Promise.all([
                apiGet(`/api/tasks/list.php?from=${from}&to=${to}&status=open&date_field=target`),
                apiGet(`/api/tasks/list.php?from=${from}&to=${to}&status=done&date_field=completed`),
                apiGet(`/api/tasks/dots.php?month=${monthKey}`).catch(() => ({ dots: {} })),
            ]);

            setTasksOpen((open.tasks || []).map(mapTaskRowToWidgetItem));
            setTasksDone((done.tasks || []).map((t) => ({
                ...mapTaskRowToWidgetItem(t),
                completedAt: t.completed_at ? new Date(t.completed_at).getTime() : Date.now(),
            })));

            // dots: { "YYYY-MM-DD": {count, tone(1..4)} }
            const m = new Map();
            const obj = dots.dots || {};
            Object.keys(obj).forEach((day) => {
                const toneNum = Number(obj[day]?.tone || 2);
                const tone =
                    toneNum >= 4 ? "red" : toneNum === 3 ? "orange" : "blue";
                m.set(day, tone);
            });
            setDotsMap(m);
        } catch (e) {
            setErr(e.message || "Errore caricamento");
            setTasksOpen([]);
            setTasksDone([]);
            setDotsMap(new Map());
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        reloadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [monthKey]);

    async function handleAddTask(dateStr, payload) {
        // payload: { label, priority: normal|medium|high }
        const title = (payload?.label || "").trim();
        if (!title) return;

        await apiPost("/api/tasks/create.php", {
            title,
            description: (payload?.description || "").trim() || null,
            target_date: dateStr,
            due_date: null,
            priority: mapPriorityToDb(payload?.priority),
            source_module: "dashboard",
            source_ref: null,
            visibility: "company",
        });

        await reloadAll();
    }

    async function handleCompleteTask(item) {
        const actionUrl = taskActionUrl(item, true);
        if (actionUrl) {
            window.location.href = actionUrl;
            return;
        }

        await apiPost("/api/tasks/complete.php", { id: item.id });
        await reloadAll();
    }

    async function handleRestoreTask(item) {
        await apiPost("/api/tasks/restore.php", { id: item.id });
        await reloadAll();
    }

    // opzionale: apri dettaglio
    function handleOpenTask(item) {
        const actionUrl = taskActionUrl(item);
        if (actionUrl) {
            window.location.href = actionUrl;
            return;
        }

        // per ora niente modal: puoi aprire una sidebar o navigare a /admin?tab=scadenze&id=...
        console.log("OPEN TASK", item);
    }

    return (
        <div>
            {err ? (
                <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {err}
                </div>
            ) : null}

            {loading ? (
                <div className="mb-3 text-sm text-slate-500">Caricamento attività…</div>
            ) : null}

            <AttivitaScadenzeWidget
                tasks={tasksOpen}
                done={tasksDone}
                scadenzeByDate={dotsMap}
                onOpenTask={handleOpenTask}
                onCompleteTask={handleCompleteTask}
                onRestoreTask={handleRestoreTask}
                onAddTask={handleAddTask}
                onMonthChange={setMonth}
            />
        </div>
    );
}
